import { create } from 'zustand';
import React from 'react';
import _, { isEqual, set as setPath } from 'lodash';
import { StoreState } from './types';

type PlainObject = Record<string, any>;

// Recursive function to remove properties set to null
const removeNulls = (obj: PlainObject): PlainObject => {
  return _.mapValues(obj, (value) => {
    if (_.isObject(value) && !_.isArray(value)) {
      // Recursively process nested objects
      return removeNulls(value);
    }
    return value;
  });
};

// Wrapper function to clean the object
const cleanObject = (obj: PlainObject): PlainObject => {
  // First, remove null values
  const cleaned = _.omitBy(removeNulls(obj), _.isNil);
  // Optionally, remove empty objects
  return _.omitBy(cleaned, (value) => _.isObject(value) && _.isEmpty(value));
};

export const useStore = create<StoreState>((set) => ({
  dataGridState: {},
  setDataGridState: (dataGridId: string, data: any) => set((state) => ({
    dataGridState: {
      ...(state.dataGridState ?? {}),
      [dataGridId]: data,
    },
  })),
  dataGridInput: {},
  setDataGridInput: (dataGridId: string, data: any) => set((state) => ({
    dataGridInput: {
      ...(state.dataGridInput ?? {}),
      [dataGridId]: data,
    },
  })),
  dataGridOutput: {},
  setDataGridOutput: (dataGridId: string, data: any) => set((state) => ({
    dataGridOutput: {
      ...(state.dataGridOutput ?? {}),
      [dataGridId]: data,
    },
  })),
  globalFields: {
    autoComplete: {},
    sync: {
      status: 'uninitialized',
      data: {},
    }
  },
  getGlobalFieldsAutoComplete: (state: StoreState) =>
    state.globalFields.autoComplete,
  resetGlobalFields: () => set(() => ({
    globalFields: {
      autoComplete: {},
      sync: {
        status: 'uninitialized',
        data: {},
      }
    },
  })),
  setGlobalFieldsSyncStatus: (status: 'stale' | 'current' | 'uninitialized') => set((state) => ({
    globalFields: {
      ...(state.globalFields ?? {}),
      sync: {
        status,
        data: state.globalFields?.sync?.data ?? {},
      },
    },
  })),
  setGlobalFieldsStaleSyncData: (data: any) => set((state) => ({
    globalFields: {
      ...(state.globalFields ?? {}),
      sync: {
        status: 'stale',
        data,
      },
    },
  })),
  initializeGlobalFields: (data: any) => set((state) => ({
    globalFields: {
      ...(state.globalFields ?? {}),
      autoComplete: data,
      sync: {
        status: 'current',
        data: {},
      },
    },
  })),
  updateGlobalFieldWithSyncData: () => set((state) => ({
    globalFields: {
      ...(state.globalFields ?? {}),
      autoComplete: state.globalFields.sync.data ?? state.globalFields.autoComplete,
      sync: {
        status: 'current',
        data: {}
      },
    },
  })),
  setGlobalFieldRules: (data: any) => set((state) => ({
    globalFields: {
      ...(state.globalFields ?? {}),
      autoComplete: data,
    },
  })),
  formDataLink: {},
  setFormDataLink: (formId: string, data: any) => set((state) => ({
    formDataLink: {
      ...(state.formDataLink ?? {}),
      [formId]: data,
    },
  })),
  pageLayoutProps: null,
  setPageLayoutProps: (props) => set((state) => ({
    pageLayoutProps: {
      title: props?.title ?? state.pageLayoutProps?.title ?? '',
      description: props?.description ?? state.pageLayoutProps?.description ?? '',
      containerSize: props?.containerSize ?? state.pageLayoutProps?.containerSize ?? '',
    },
  })),
  savedFormDrafts: {},
  saveFormDraft: (formId: string, data: any) => set((state) => ({
    savedFormDrafts: {
      ...(state.savedFormDrafts ?? {}),
      [formId]: data,
    },
  })),
  availabilityData: {},
  setAvailabilityData: (data: any) => set((state) => ({
    availabilityData: data ?? {},
  })),
}));

export const clearData = () => {
  useStore.getState().dataGridOutput = {};
  useStore.getState().dataGridInput = {};
  useStore.getState().formDataLink = {};
  useStore.getState().savedFormDrafts = {};
  useStore.getState().globalFields = {
    autoComplete: {},
    sync: {
      status: 'uninitialized',
      data: {},
    }
  };
  useStore.getState().pageLayoutProps = null;
}

export const getSavedFormDraft = (formId: string): { required_form_data: any, optional_form_data?: any } | null => useStore.getState().savedFormDrafts?.[formId];
export const getGlobalFieldsSyncStatus = (): StoreState['globalFields']['sync'] => (useStore.getState().globalFields.sync);
export const setGlobalFieldsSyncStatus = (status: 'stale' | 'current'): void => useStore.getState().setGlobalFieldsSyncStatus(status);
export const resetGlobalFields = (): void => useStore.getState().resetGlobalFields();
export const setGlobalFieldsStaleSyncData = (data: any): void => useStore.getState().setGlobalFieldsStaleSyncData(data);
export const initializeGlobalFields = (data: any): void => useStore.getState().initializeGlobalFields(data);
export const updateGlobalFieldWithSyncData = (): void => useStore.getState().updateGlobalFieldWithSyncData();
export const getGlobalFieldsAutoComplete = (): StoreState['globalFields']['autoComplete'] => (useStore.getState().globalFields.autoComplete);

export const setDataGridInput = (dataGridId?: string, data?: any) => {
  if (dataGridId) {
    const prevData = useStore.getState().dataGridInput[dataGridId];
    if (isEqual(prevData, data)) return;
    useStore.getState().setDataGridOutput(dataGridId, data ?? {});
  }
};

export const setDataGridOutput = (dataGridId?: string, data?: any) => {
  if (dataGridId) {
    const prevData = useStore.getState().dataGridOutput[dataGridId];
    if (isEqual(prevData, data)) return;
    useStore.getState().setDataGridInput(dataGridId, data ?? {});
  }
};

export const setRequiredLinkedFormData = (globalFormId?: string, data?: any, merge: boolean = false) => {
  if (globalFormId) {
    if (merge) {
      const current = useStore.getState().formDataLink[globalFormId] ?? {};
      useStore.getState().setFormDataLink(globalFormId, { ...current, ...data });
    } else {
      useStore.getState().setFormDataLink(globalFormId, data ?? {});
    }
  }
};

export const getRequiredLinkedFormData = (globalFormId?: string) => {
  if (globalFormId) {
    return useStore.getState().formDataLink?.[globalFormId];
  }
  return {};
};

export const useDataGrid = (dataGridId?: string): {
  dataGridInput: any,
  setDataGridInput: (data: any) => void,
  dataGridOutput: any,
  setDataGridOutput: (data: any) => void,
} => {
  const dataGridInput = useStore((state) => (dataGridId ? state.dataGridInput[dataGridId] : null));
  const dataGridOutput = useStore((state) => (dataGridId ? state.dataGridOutput[dataGridId] : null));

  const setDataGridInput = React.useCallback((data: any) => {
    if (dataGridId) {
      useStore.getState().setDataGridInput(dataGridId, data ?? {});
    }
  }, [dataGridId]);

  const setDataGridOutput = React.useCallback((data: any) => {
    if (dataGridId) {
      useStore.getState().setDataGridOutput(dataGridId, data ?? {});
    }
  }, [dataGridId]);

  return {
    dataGridInput,
    setDataGridInput,
    dataGridOutput,
    setDataGridOutput,
  };
}

export const useFormDataLink = ({ requiredFormId, optionalFormId }: { requiredFormId?: string, optionalFormId?: string }): {
  requiredLinkedFormData: any;
  optionalLinkedFormData: any;
  setRequiredLinkedFormData: (data?: any) => void;
  setOptionalLinkedFormData: (data?: any) => void;
} => {
  const requiredLinkedFormData = useStore((state) => (requiredFormId ? state.formDataLink[requiredFormId] : null));
  const optionalLinkedFormData = useStore((state) => (optionalFormId ? state.formDataLink[optionalFormId] : null));
  const setRequiredLinkedFormData = React.useCallback((data: any) => {
    if (requiredFormId) {
      useStore.getState().setFormDataLink(requiredFormId, data ?? {});
    }
  }, [requiredFormId]);
  const setOptionalLinkedFormData = React.useCallback((data: any) => {
    if (optionalFormId) {
      useStore.getState().setFormDataLink(optionalFormId, data ?? {});
    }
  }, [optionalFormId]);

  return {
    requiredLinkedFormData,
    optionalLinkedFormData,
    setRequiredLinkedFormData,
    setOptionalLinkedFormData,
  };
};
